import { parse as parseYaml } from "yaml";
import { z } from "zod";

/**
 * Repository vocabulary, so a guide can reference a domain concept instead of redefining it.
 *
 * The point is not to save the author typing. It is that pre-code word budget is the scarce
 * resource — a definition costs ~40 words every time, a reference costs five — and that a shared
 * term list makes "one noun per concept" checkable against what the team actually says rather
 * than against guesses baked into the linter.
 */
const termSchema = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
  term: z.string().min(1),
  /** Other words the team uses for this concept. Feeds synonym-drift detection. */
  aka: z.array(z.string().min(1)).default([]),
  definition: z.string().min(1),
  /** Why the concept exists. Optional: many terms are self-evident once defined. */
  purpose: z.string().min(1).optional(),
  seeAlso: z.array(z.string().min(1)).default([]),
});

export const glossarySchema = z.object({
  schemaVersion: z.literal(1),
  terms: z.array(termSchema).default([]),
});

export type GlossaryTerm = z.infer<typeof termSchema>;
export type Glossary = z.infer<typeof glossarySchema>;

export const emptyGlossary: Glossary = { schemaVersion: 1, terms: [] };

export function parseGlossaryYaml(source: string, additionalTermIds: Iterable<string> = []): Glossary {
  const parsed = glossarySchema.parse(parseYaml(source));
  const seen = new Set<string>(additionalTermIds);
  const local = new Set<string>();
  for (const term of parsed.terms) {
    if (local.has(term.id)) throw new Error(`duplicate glossary term '${term.id}'`);
    local.add(term.id);
    seen.add(term.id);
  }
  for (const term of parsed.terms) {
    for (const related of term.seeAlso) {
      if (!seen.has(related)) throw new Error(`glossary term '${term.id}' links unknown term '${related}'`);
    }
  }
  return parsed;
}

/** Apply repository vocabulary over CHEW's centrally versioned glossary. */
export function mergeGlossaries(base: Glossary, overlay: Glossary): Glossary {
  const overlayById = new Map(overlay.terms.map((term) => [term.id, term]));
  const baseIds = new Set(base.terms.map((term) => term.id));
  const terms = [
    ...base.terms.map((term) => overlayById.get(term.id) ?? term),
    ...overlay.terms.filter((term) => !baseIds.has(term.id)),
  ];
  const known = new Set(terms.map((term) => term.id));
  for (const term of terms) {
    for (const related of term.seeAlso) {
      if (!known.has(related)) throw new Error(`glossary term '${term.id}' links unknown term '${related}'`);
    }
  }
  return { schemaVersion: 1, terms };
}

export const termHrefPrefix = "chew://term/";

export function termHref(id: string): string {
  return `${termHrefPrefix}${id}`;
}

/** The term id a markdown href points at, or undefined when it is not a term link. */
export function parseTermHref(href: string): string | undefined {
  if (!href.startsWith(termHrefPrefix)) return undefined;
  const id = href.slice(termHrefPrefix.length).trim();
  return /^[a-z0-9][a-z0-9-]*$/.test(id) ? id : undefined;
}

export function findTerm(glossary: Glossary, id: string): GlossaryTerm | undefined {
  return glossary.terms.find((term) => term.id === id);
}

/** Every term id referenced by a body of guide copy. */
export function referencedTermIds(values: Iterable<string>): Set<string> {
  const ids = new Set<string>();
  for (const value of values) {
    for (const match of value.matchAll(/\]\((chew:\/\/term\/[a-z0-9][a-z0-9-]*)\)/g)) {
      const id = parseTermHref(match[1]!);
      if (id) ids.add(id);
    }
  }
  return ids;
}

/**
 * Words that name one concept, derived from the repository's own vocabulary. Replaces the
 * hardcoded guesses the copy linter previously shipped with.
 */
export function synonymClustersFrom(glossary: Glossary): string[][] {
  return glossary.terms
    .map((term) => [term.term, ...term.aka].map((word) => word.toLowerCase()))
    .filter((cluster) => cluster.length > 1);
}
